import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FootballRivalsOutcome } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { TaglineTemplate } from '@sorare/core/src/components/rivals/TaglineTemplate';
import Avatar from '@sorare/core/src/components/user/Avatar';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { Scores_footballRivalsContestant } from './__generated__/index.graphql';

const Root = styled(Horizontal).attrs({ center: true })`
  width: 100%;
  isolation: isolate;
`;
const Tagline = styled(TaglineTemplate)`
  background: rgba(var(--c-rgb-neutral-100), 0.54);
  border: none;
  backdrop-filter: blur(36px);

  &.winner {
    background: linear-gradient(
      to bottom,
      var(--c-rivals-friendlies) 0%,
      var(--c-rivals-win) 100%
    );
    color: var(--c-neutral-100);
  }
`;
const HomeTaglineWrapper = styled.div`
  transform: translate(20px, -20px);
`;
const AwayTaglineWrapper = styled.div`
  transform: translate(-20px, 20px);
`;
const AvatarWrapper = styled(Horizontal).attrs({ center: true })`
  border-radius: 50%;
  border: 2px solid var(--c-rivals-friendlies);
  z-index: 1;
`;

type Props = {
  homeContestant: Scores_footballRivalsContestant;
  awayContestant: Scores_footballRivalsContestant;
};
export const Scores = ({ homeContestant, awayContestant }: Props) => {
  const homeContestantWinner =
    homeContestant.outcome !== FootballRivalsOutcome.LOSE;
  const winnerUser = homeContestantWinner
    ? homeContestant.manager?.user
    : awayContestant.manager?.user;

  return (
    <Root>
      <HomeTaglineWrapper>
        <Tagline className={classNames({ winner: homeContestantWinner })}>
          <FormattedMessage
            {...fantasy.pointsWithoutFormat}
            values={{
              score: homeContestant.score,
            }}
          />
        </Tagline>
      </HomeTaglineWrapper>
      <AvatarWrapper>
        {winnerUser && <Avatar size={5} user={winnerUser} rounded />}
      </AvatarWrapper>
      <AwayTaglineWrapper>
        <Tagline className={classNames({ winner: !homeContestantWinner })}>
          <FormattedMessage
            {...fantasy.pointsWithoutFormat}
            values={{
              score: awayContestant.score,
            }}
          />
        </Tagline>
      </AwayTaglineWrapper>
    </Root>
  );
};

Scores.fragments = {
  footballRivalsContestant: gql`
    fragment Scores_footballRivalsContestant on FootballRivalsContestant {
      id
      score
      outcome
      manager {
        id
        user {
          slug
          ...Avatar_user
        }
      }
    }
    ${Avatar.fragments.user}
  ` as TypedDocumentNode<Scores_footballRivalsContestant>,
};
